import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Sparkles, Loader2, ShieldAlert, FileCode, CheckCircle2 } from 'lucide-react'
import { masterRunbooksApi, type TaskCodeSet, type GenerateCodeResult } from '../api'
import { PhaseSteps, appendPhase } from '@/components/ui/phase-steps'
import { useToast } from '@/hooks/use-toast'
import { useTranslation } from 'react-i18next'
import { findMethod, AutomationOptionLabel } from '@/features/objects/utils/runbookAutomation'

interface GenerateCodeDialogProps {
    masterId: number
    taskNo: number
    method: string
    /** The task as it is on screen — generation does not require a save first. */
    task?: string
    commands?: string
    open: boolean
    onOpenChange: (open: boolean) => void
}

/**
 * Author-time script generation for a master runbook task. Generates a code set WITH
 * placeholders (the object tier binds them later), shows the files for review, and surfaces
 * any credential warning. The generated content never holds a real secret — the model is
 * told to use <cred:NAME>, and an obvious inline secret is flagged here.
 */
export function GenerateCodeDialog({ masterId, taskNo, method, task, commands, open, onOpenChange }: GenerateCodeDialogProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const methodOption = findMethod(method)
    const queryClient = useQueryClient()
    const [activeFile, setActiveFile] = useState<string | null>(null)
    const [phases, setPhases] = useState<string[]>([])

    // Existing generated code (if any) so reopening shows the current set.
    const { data: existing, isLoading } = useQuery<TaskCodeSet | null>({
        queryKey: ['master-task-code', masterId, taskNo],
        queryFn: async () => (await masterRunbooksApi.getTaskCode(masterId, taskNo)).data?.data?.set ?? null,
        enabled: open,
        refetchOnWindowFocus: false,
    })

    const gen = useMutation({
        mutationFn: async () => {
            setPhases([])
            return masterRunbooksApi.generateTaskCodeStreamed(
                masterId, taskNo, { method, task, commands },
                (next) => setPhases((prev) => appendPhase(prev, next)),
            )
        },
        onSuccess: (r) => {
            queryClient.invalidateQueries({ queryKey: ['master-task-code', masterId, taskNo] })
            if (r.warnings?.length) {
                toast({ title: 'Generated with warnings', description: `${r.warnings.length} possible inline secret(s) — review before use.`, variant: 'destructive' })
            } else {
                toast({
                    title: r.status === 'verified' ? 'Script generated and verified' : 'Script generated',
                    description: `${r.fileCount} file(s) for ${t(methodOption.labelKey)}${r.verifyMethod ? ` · ${r.verifyMethod}` : ''}.`,
                })
            }
        },
        onError: (e: any) => toast({
            title: 'Generation failed',
            description: e?.response?.data?.message || e?.response?.data?.error || e.message,
            variant: 'destructive',
        }),
    })

    const result = gen.data
    const set: TaskCodeSet | null = result
        ? { entryFile: result.entryFile, format: result.format, description: result.description, status: result.status, verifyMethod: result.verifyMethod, verifiedAt: null, version: 1, files: result.files }
        : existing

    useEffect(() => {
        if (set?.files?.length) setActiveFile(set.entryFile ?? set.files[0].filePath)
    }, [set?.entryFile, set?.files?.length])

    const warnings = result?.warnings ?? []
    const review = result?.review ?? null
    const active = set?.files?.find((f) => f.filePath === activeFile) ?? set?.files?.[0]

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                        Generate script — task {taskNo}
                        <Badge variant="outline" className="gap-1">
                            <AutomationOptionLabel option={methodOption} label={t(methodOption.labelKey)} />
                        </Badge>
                    </DialogTitle>
                    <DialogDescription>
                        AI generates the script with placeholders (bound per object later). Credentials use <code>&lt;cred:…&gt;</code> and are never stored. Works on the task as it is on screen — no need to save first; the script is stored against task {taskNo} and is there when you save.
                    </DialogDescription>
                </DialogHeader>

                {phases.length > 0 && (
                    <div className="rounded-lg border bg-muted/30 px-3 py-2.5">
                        <PhaseSteps phases={phases} done={!gen.isPending} />
                    </div>
                )}

                {isLoading && !set && (
                    <div className="py-8 flex items-center justify-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…</div>
                )}

                {warnings.length > 0 && (
                    <div className="rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/20 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
                        <div className="flex items-center gap-2 font-medium"><ShieldAlert className="h-4 w-4" /> Possible inline secrets — review</div>
                        <ul className="mt-1 pl-6 list-disc text-xs">
                            {warnings.map((w, i) => <li key={i}><span className="font-mono">{w.file}</span>: {w.name}</li>)}
                        </ul>
                    </div>
                )}

                {review && review.issues.length > 0 && (
                    <div className={`rounded-lg border px-3 py-2 text-sm ${review.verdict === 'pass'
                        ? 'border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-400'
                        : 'border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400'}`}>
                        <div className="flex items-center gap-2 font-medium">
                            <ShieldAlert className="h-4 w-4" />
                            {review.verdict === 'pass'
                                ? 'Review passed with notes'
                                : 'Review failed — this script stays a draft'}
                        </div>
                        <ul className="mt-1 pl-6 list-disc text-xs space-y-0.5">
                            {review.issues.map((i, n) => (
                                <li key={n}>
                                    <span className="uppercase opacity-70">{i.severity}</span>
                                    {i.file ? <span className="font-mono"> {i.file}</span> : null} — {i.detail}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {set?.files?.length ? (
                    <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                            {set.status === 'verified'
                                ? <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"><CheckCircle2 className="h-3 w-3 mr-1" />verified</Badge>
                                : <Badge variant="secondary">draft</Badge>}
                            {set.verifyMethod && <span className="text-muted-foreground">{set.verifyMethod}</span>}
                            {result?.attempts != null && result.attempts > 1 && (
                                <span className="text-muted-foreground">· {result.attempts} attempts</span>
                            )}
                            {set.description && <span className="text-muted-foreground">— {set.description}</span>}
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {set.files.map((f) => (
                                <button
                                    key={f.filePath}
                                    onClick={() => setActiveFile(f.filePath)}
                                    className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-mono border ${f.filePath === active?.filePath ? 'bg-muted border-ring' : 'border-transparent hover:bg-muted'}`}
                                >
                                    <FileCode className="h-3 w-3" />{f.filePath}{f.filePath === set.entryFile ? ' ·entry' : ''}
                                </button>
                            ))}
                        </div>
                        <pre className="text-xs bg-muted rounded-md p-3 max-h-72 overflow-auto whitespace-pre-wrap">{active?.content ?? ''}</pre>
                    </div>
                ) : !isLoading && !gen.isPending ? (
                    <div className="py-6 text-sm text-muted-foreground">No script yet. Generate one for this task.</div>
                ) : null}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
                    <Button onClick={() => gen.mutate()} disabled={gen.isPending}>
                        {gen.isPending
                            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</>
                            : <><Sparkles className="h-4 w-4 mr-2" /> {set?.files?.length ? 'Regenerate' : 'Generate'}</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
